import type { ReactNode } from 'react';

/**
 * Small line icons for the main navigation (DESIGN.md §6.1). Drawn here, 24px grid,
 * 1.6px stroke in the current colour, so no icon font or package is needed. They sit
 * beside a text label and are never the only way to tell items apart.
 */
const PATHS = {
  home: (
    <>
      <path d="M3.5 10.5 12 3.5l8.5 7" />
      <path d="M5.5 9v11h13V9" />
      <path d="M10 20v-5.5h4V20" />
    </>
  ),
  reception: (
    <>
      <circle cx="12" cy="8" r="3.5" />
      <path d="M4.5 20.5c.6-3.9 3.8-6.5 7.5-6.5s6.9 2.6 7.5 6.5" />
    </>
  ),
  appointments: (
    <>
      <rect x="3.5" y="5" width="17" height="15.5" rx="2" />
      <path d="M3.5 10h17M8 3v4M16 3v4" />
      <path d="M8 14h3" />
    </>
  ),
  queue: (
    <>
      <path d="M9 6.5h11M9 12h11M9 17.5h11" />
      <circle cx="4.75" cy="6.5" r="1" />
      <circle cx="4.75" cy="12" r="1" />
      <circle cx="4.75" cy="17.5" r="1" />
    </>
  ),
  lab: (
    <>
      <path d="M9 3.5h6M10 3.5v6L4.8 18.6A1.6 1.6 0 0 0 6.2 21h11.6a1.6 1.6 0 0 0 1.4-2.4L14 9.5v-6" />
      <path d="M7.2 15h9.6" />
    </>
  ),
  casualty: (
    <>
      <rect x="3.5" y="3.5" width="17" height="17" rx="3" />
      <path d="M12 8v8M8 12h8" />
    </>
  ),
  imaging: (
    <>
      <rect x="3.5" y="3.5" width="17" height="17" rx="2" />
      <path d="M12 6.5v11M8.5 9h7M8 12h8M8.5 15h7" />
    </>
  ),
  wards: (
    <>
      <path d="M3.5 6v13M3.5 15.5h17V19" />
      <path d="M3.5 12h6v3.5M9.5 12h8.5a2.5 2.5 0 0 1 2.5 2.5v1" />
      <circle cx="6.5" cy="9.5" r="1.5" />
    </>
  ),
  theatre: (
    <>
      <path d="M12 3.5v3" />
      <path d="M4.5 14a7.5 6.5 0 0 1 15 0z" />
      <path d="M12 14v6.5M8.5 20.5h7" />
    </>
  ),
  pharmacy: (
    <>
      <path d="M10.2 20.3a4.9 4.9 0 0 1-6.9-6.9l7.4-7.4a4.9 4.9 0 0 1 6.9 6.9z" />
      <path d="m7.4 9.2 7.3 7.3" />
    </>
  ),
  stores: (
    <>
      <path d="M3.5 7.5 12 3.5l8.5 4v9L12 20.5l-8.5-4z" />
      <path d="M3.5 7.5 12 11.5l8.5-4M12 11.5v9" />
    </>
  ),
  cashier: (
    <>
      <rect x="2.5" y="6" width="19" height="12" rx="2" />
      <circle cx="12" cy="12" r="2.5" />
      <path d="M6 12h.01M18 12h.01" />
    </>
  ),
  approvals: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="m8.5 12.2 2.4 2.4 4.6-5.2" />
    </>
  ),
  claims: (
    <>
      <path d="M14 3.5H6.5a1 1 0 0 0-1 1v15a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1V8z" />
      <path d="M14 3.5V8h4.5M9 13h6M9 16.5h6" />
    </>
  ),
  insurance: (
    <>
      <path d="M12 3.5 5.5 6v5.5c0 4.2 2.8 7.5 6.5 9 3.7-1.5 6.5-4.8 6.5-9V6z" />
      <path d="M9 12h6M12 9v6" />
    </>
  ),
  // A ledger: journals for the accounting package (docs/plans/phase3-accounting-and-revenue-share.md §6).
  accounting: (
    <>
      <path d="M5.5 4.5h11a2 2 0 0 1 2 2v13h-11a2 2 0 0 1-2-2z" />
      <path d="M5.5 17.5a2 2 0 0 1 2-2h11M9 8.5h6M9 11.5h6" />
    </>
  ),
  // Stacked coins: a practitioner's revenue share.
  earnings: (
    <>
      <ellipse cx="12" cy="6.5" rx="6.5" ry="2.5" />
      <path d="M5.5 6.5v5c0 1.4 2.9 2.5 6.5 2.5s6.5-1.1 6.5-2.5v-5" />
      <path d="M5.5 11.5v5c0 1.4 2.9 2.5 6.5 2.5s6.5-1.1 6.5-2.5v-5" />
    </>
  ),
  // A folder: the medical records office (docs/plans/phase2-records-data-rights.md §6).
  records: (
    <>
      <path d="M3.5 7.5v11a1 1 0 0 0 1 1h15a1 1 0 0 0 1-1v-9a1 1 0 0 0-1-1h-8l-2-2.5h-5a1 1 0 0 0-1 1z" />
      <path d="M8 14h8" />
    </>
  ),
  // Two figures, one smaller: maternity and child health (docs/plans/phase2-maternity-mch.md §6).
  maternity: (
    <>
      <circle cx="9" cy="6.5" r="2.5" />
      <path d="M5 20v-5.5a4 4 0 0 1 8 0V20" />
      <circle cx="17" cy="11" r="1.8" />
      <path d="M14.5 20v-3a2.5 2.5 0 0 1 5 0v3" />
    </>
  ),
  referrals: (
    <>
      <rect x="3.5" y="5" width="7" height="14" rx="1.5" />
      <rect x="13.5" y="5" width="7" height="14" rx="1.5" />
      <path d="M8 12h7.5M13 9.5 15.5 12 13 14.5" />
    </>
  ),
  // A cold-room door with its handle: the mortuary
  // (docs/plans/phase2-mortuary-and-notifications.md §6).
  mortuary: (
    <>
      <rect x="5" y="3.5" width="14" height="17" rx="1.5" />
      <path d="M5 12h14M15.5 7.5v2M15.5 14.5v2" />
    </>
  ),
  // An identity badge: staff records (docs/plans/phase2-staff-records.md §6).
  staff: (
    <>
      <rect x="4" y="4.5" width="16" height="15" rx="2" />
      <circle cx="9.5" cy="10.5" r="2" />
      <path d="M6.5 16.5c.4-1.8 1.6-2.8 3-2.8s2.6 1 3 2.8M14.5 9.5h3M14.5 13h3" />
    </>
  ),
  // The operations modules (docs/plans/phase3-operations-modules.md): a flag for incidents,
  // a shield for infection control, a wrench for assets, a calendar with a clock for the roster.
  incidents: (
    <>
      <path d="M5.5 21V4" />
      <path d="M5.5 4.5h11l-2.5 4 2.5 4h-11" />
    </>
  ),
  infection: (
    <>
      <path d="M12 3.5 5 6v5.5c0 4.2 3 7.6 7 9 4-1.4 7-4.8 7-9V6z" />
      <path d="M9 12h6M12 9v6" />
    </>
  ),
  assets: (
    <>
      <path d="M14.5 5.5a4 4 0 0 0 4.9 4.9l-9 9a2.1 2.1 0 0 1-3-3l9-9a4 4 0 0 0-1.9-1.9z" />
      <path d="M15.5 3.8a4 4 0 0 1 4.7 4.7" />
    </>
  ),
  roster: (
    <>
      <rect x="3.5" y="5" width="12" height="12" rx="2" />
      <path d="M3.5 9h12M7 3v4M12 3v4" />
      <circle cx="16.5" cy="16.5" r="4" />
      <path d="M16.5 14.5v2l1.5 1" />
    </>
  ),
  // Kitchen and housekeeping (docs/plans/phase3-kitchen-and-housekeeping.md): a covered dish,
  // a bucket.
  kitchen: (
    <>
      <path d="M3.5 16.5h17" />
      <path d="M5 16.5a7 7 0 0 1 14 0" />
      <path d="M12 7.5V6M10.5 6h3M5.5 19.5h13" />
    </>
  ),
  housekeeping: (
    <>
      <path d="M5.5 9.5h13l-1.5 11h-10z" />
      <path d="M7 9.5a5 5 0 0 1 10 0" />
      <path d="M9 14h6" />
    </>
  ),
  reports: (
    <>
      <path d="M3.5 20.5h17" />
      <path d="M7 17v-5M12 17V7M17 17v-8" />
    </>
  ),
  settings: (
    <>
      <path d="M4 7h9M17 7h3M4 12h3M11 12h9M4 17h11M19 17h1" />
      <circle cx="15" cy="7" r="2" />
      <circle cx="9" cy="12" r="2" />
      <circle cx="17" cy="17" r="2" />
    </>
  ),
  sidebar: (
    <>
      <rect x="3.5" y="4.5" width="17" height="15" rx="2" />
      <path d="M9.5 4.5v15" />
    </>
  ),
} satisfies Record<string, ReactNode>;

export type NavIconName = Exclude<keyof typeof PATHS, 'sidebar'>;

export function NavIcon({ name, className }: { name: keyof typeof PATHS; className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {PATHS[name]}
    </svg>
  );
}
